import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Tooltip,
  Popconfirm,
  MenuProps,
  Form,
  Input,
  Select,
  Col,
  Image,
  Upload,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  StopOutlined,
  UploadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import PageHeader from '../../components/common/PageHeader';
import FilterPanel from '../../components/common/FilterPanel';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import {
  SectionTypeDTO,
  SectionTypeFilters,
  SectionTypeStatus,
} from '../../types/sectionType';
import sectionTypeService from '../../services/sectionTypeService';
import {
  SECTION_TYPE_STATUS_OPTIONS,
} from '../../utils/sectionTypeConstants';
import { formatTimestamp } from '../../utils/responseTransformer';
import './sectionType.css';

const { confirm } = Modal;

interface SectionTypeListProps {
  // 可以添加额外的props
}

const SectionTypeList: React.FC<SectionTypeListProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<SectionTypeDTO[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 用于手动触发刷新
  const [filters, setFilters] = useState<SectionTypeFilters>({
    status: 'all' as SectionTypeStatus | 'all' | undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 导入功能状态
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // 获取数据
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await sectionTypeService.pageQuerySectionTypes({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        status: filters.status === 'all' ? undefined : filters.status,
        // 如果有搜索条件，则同时设置到 typeCode 和 typeName 字段进行模糊匹配
        typeCode: filters.typeCode,
        typeName: filters.typeName,
      });

      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination((prev: any) => ({
          ...prev,
          total: response.data.paging.totalCount,
        }));
      } else {
        message.error(response.errorMessage || t('sectionType.queryFailed'));
      }
    } catch (error) {
      console.error('获取货架类型列表失败:', error);
      message.error(t('sectionType.queryFailed'));
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters.status, filters.typeName, filters.typeCode, t]);

  // 初始化和刷新数据
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  // 处理分页
  const handleTableChange = (page: number, size: number) => {
    setPagination((prev: any) => ({
      ...prev,
      current: page,
      pageSize: size,
    }));
  };

  // 处理过滤器变更
  const handleFilterChange = (values: any) => {
    const newFilters = {
      typeCode: values.typeCode?.trim() || undefined,
      typeName: values.typeName?.trim() || undefined,
      status: values.status === 'all' ? undefined : values.status,
    };
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefreshTrigger(prev => prev + 1); // 触发刷新
  };

  // 处理重置
  const handleResetFilters = () => {
    setFilters({
       typeCode: undefined,
      typeName: undefined,
      status: undefined,
    });
    // 清空选中的行
    setSelectedRowKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefreshTrigger(prev => prev + 1); // 触发刷新
  };

  // 创建新记录
  const handleCreate = () => {
    navigate('/section-type/create');
  };

  // 编辑记录
  const handleEdit = (record: SectionTypeDTO) => {
    if (record.status !== SectionTypeStatus.DRAFT) {
      message.warning(t('sectionType.onlyDraftCanEdit'));
      return;
    }
    navigate(`/section-type/edit/${record.id}`);
  };

  // 查看详情
  const handleView = (record: SectionTypeDTO) => {
    navigate(`/section-type/detail/${record.id}`);
  };

  // 删除单个记录
  const handleDelete = (record: SectionTypeDTO) => {
    if (record.status !== SectionTypeStatus.DRAFT) {
      message.warning(t('sectionType.onlyDraftCanDelete'));
      return;
    }

    confirm({
      title: t('sectionType.confirmDelete'),
      content: t('sectionType.confirmDeleteMessage', { name: record.typeName }),
      icon: <ExclamationCircleOutlined />,
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await sectionTypeService.deleteSectionType({ id: record.id });
          if (response.success) {
            // message.success(SECTION_TYPE_MESSAGES.DELETE_SUCCESS);
            fetchData();
          } else {
            message.error(response.errorMessage || t('sectionType.deleteFailed'));
          }
        } catch (error) {
          console.error('删除货架类型失败:', error);
          message.error(t('sectionType.deleteFailed'));
        }
      },
    });
  };

  // 批量操作
  const handleBatchOperation = async (operation: 'enable' | 'disable' | 'delete') => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('sectionType.pleaseSelectItems'));
      return;
    }

    const ids = selectedRowKeys.map(key => Number(key));
    
    // 检查删除权限
    if (operation === 'delete') {
      const draftItems = dataSource.filter(item => 
        selectedRowKeys.includes(item.id) && item.status === SectionTypeStatus.DRAFT
      );
      if (draftItems.length !== selectedRowKeys.length) {
        message.warning(t('sectionType.onlyDraftCanDelete'));
        return;
      }
    }

    const operationTexts = {
      enable: t('sectionType.enable'),
      disable: t('sectionType.disable'),
      delete: t('sectionType.delete'),
    };

    confirm({
      title: t('sectionType.batchOperation'),
      content: t('sectionType.batchOperationConfirm', { 
        action: operationTexts[operation], 
        count: selectedRowKeys.length 
      }),
      icon: <ExclamationCircleOutlined />,
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          let response;
          switch (operation) {
            case 'enable':
              response = await sectionTypeService.batchEnableSectionTypes(ids);
              break;
            case 'disable':
              response = await sectionTypeService.batchDisableSectionTypes(ids);
              break;
            case 'delete':
              // 暂时使用循环调用单个删除接口
              let deleteSuccessCount = 0;
              for (const id of ids) {
                const deleteResponse = await sectionTypeService.deleteSectionType({ id });
                if (deleteResponse.success) {
                  deleteSuccessCount++;
                }
              }
              response = {
                success: deleteSuccessCount > 0,
                data: {
                  successCount: deleteSuccessCount,
                  failureCount: ids.length - deleteSuccessCount,
                  totalCount: ids.length
                }
              };
              break;
          }

          if (response.success) {
            message.success(t('sectionType.batchOperationSuccess', { 
                action: operationTexts[operation], 
              }));
            // 清空选中的行
            setSelectedRowKeys([]);
            // 刷新数据
            setRefreshTrigger(prev => prev + 1);
          } else {
            message.error(response.errorMessage || t('sectionType.batchOperationFailed', { 
              action: operationTexts[operation] 
            }));
          }
        } catch (error) {
          console.error(`批量${operationTexts[operation]}失败:`, error);
          message.error(t('sectionType.batchOperationFailed', { 
            action: operationTexts[operation] 
          }));
        }
      },
    });
  };

  // Handle import display config
  const handleImportDisplayConfig = () => {
    setImportModalVisible(true);
  };

  // Handle import file upload
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t('sectionType.import.selectFileFirst'));
      return;
    }

    const file = fileList[0];
    setImportLoading(true);

    try {
      message.loading(t('sectionType.import.uploading'), 0);
      const response = await sectionTypeService.importSectionTypes(file);
      
      if (response.success) {
        message.destroy();
        message.success(t('sectionType.import.success'));
        setImportModalVisible(false);
        setFileList([]);
        fetchData(); // 刷新列表

        // 如果有返回文件，提供下载
        if (response.data) {
          const link = document.createElement('a');
          link.href = response.data.downloadUrl;
          link.download = response.data.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        message.destroy();
        message.error(response.errorMessage || t('sectionType.import.failed'));
      }
    } catch (error) {
      message.destroy();
      console.error('Import error:', error);
      message.error(t('sectionType.import.failed'));
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import modal cancel
  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  // Upload props
  const uploadProps = {
    accept: '.xlsx,.xls',
    fileList,
    beforeUpload: (file: any) => {
      // 验证文件类型
      const isValidType = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      if (!isValidType) {
        message.error(t('sectionType.import.invalidFileType'));
        return false;
      }
      
      // 验证文件大小 (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('sectionType.import.fileTooLarge'));
        return false;
      }
      
      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // 行选择配置
  const rowSelection: TableRowSelection<SectionTypeDTO> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // 表格列定义
  const columns: ColumnsType<SectionTypeDTO> = [
    {
      title: t('sectionType.image'),
      dataIndex: 'imageUrl',
      key: 'image',
      width: 80,
      render: (imageUrl: string, record: SectionTypeDTO) => {
        return <Image
          src={imageUrl}
          alt={``}
          width={60}
          height={45}
          className="fixture-table-image"
        />
      },
    },
    {
      title: t('sectionType.typeCode'),
      dataIndex: 'typeCode',
      key: 'typeCode',
      width: 150,
      render: (text: string, record: SectionTypeDTO) => (
        <Button
          type="link"
          onClick={() => handleView(record)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t('sectionType.typeName'),
      dataIndex: 'typeName',
      key: 'typeName',
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: SectionTypeStatus) => {
        const colorMap = {
          [SectionTypeStatus.DRAFT]: 'default',
          [SectionTypeStatus.ENABLE]: 'success',
          [SectionTypeStatus.DISABLE]: 'error',
        };
        const labelMap = {
          [SectionTypeStatus.DRAFT]: t('sectionType.statusTest.DRAFT'),
          [SectionTypeStatus.ENABLE]: t('sectionType.statusTest.ENABLE'),
          [SectionTypeStatus.DISABLE]: t('sectionType.statusTest.DISABLE'),
        };
        return (
          <Tag color={colorMap[status]}>
            {labelMap[status]}
          </Tag>
        );
      },
    },
    {
      title: t('sectionType.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('common.creator'),
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 120,
    },
    {
      title: t('common.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (timestamp: number) => formatTimestamp(timestamp),
    },
    {
      title: t('common.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      render: (timestamp: number) => formatTimestamp(timestamp),
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record: SectionTypeDTO) => (
        <Space size="large">
           <Tooltip title= {t('common.view')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
           </Tooltip>
          
          {record.status === SectionTypeStatus.DRAFT && (
            <Tooltip title= {t('common.edit')}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            
          )}
          {record.status === SectionTypeStatus.DRAFT && (
            <Popconfirm
                title={t('common.delete')}
                onConfirm={() => handleDelete(record)}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
            >
                <Button
                type="link"
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
            
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <FilterPanel
        title={t("menu.sectionType")}
        onFilter={handleFilterChange}
        onReset={handleResetFilters}
        loading={loading}
        initialValues={{
          status: filters.status || 'all',
        }}
      >
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="typeCode" label={t('sectionType.typeCode')}>
            <Input 
              placeholder={t('sectionType.searchCodePlaceholder')} 
              allowClear 
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} 
              disabled={loading}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="typeName" label={t('sectionType.typeName')}>
            <Input 
              placeholder={t('sectionType.searchNamePlaceholder')} 
              allowClear 
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} 
              disabled={loading}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="status" label={t('common.status')}>
            <Select 
              placeholder={t('common.pleaseSelect')} 
              allowClear 
              disabled={loading}
            >
              {SECTION_TYPE_STATUS_OPTIONS.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {t(option.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'enable',
                label: t('sectionType.batchEnable'),
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                onClick: () => handleBatchOperation('enable'),
              },
              {
                key: 'disable',
                label: t('sectionType.batchDisable'),
                icon: <StopOutlined style={{ color: '#faad14' }} />,
                onClick: () => handleBatchOperation('disable'),
              },
              {
                key: 'delete',
                label: t('sectionType.batchDelete'),
                danger: true,
                onClick: () => handleBatchOperation('delete'),
              }
            ]}
            onClearSelection={() => setSelectedRowKeys([])}
          />

          <Space>
            <Button
              icon={<UploadOutlined />}
              onClick={handleImportDisplayConfig}
            >
              {t('sectionType.import.title')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('sectionType.createNew')}
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          rowSelection={rowSelection}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('common.paginationTotal', { start: range[0], end: range[1], total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1200, y: 500 }}
          locale={{
            emptyText: t('sectionType.noData'),
          }}
        />
      </Card>

      {/* 导入对话框 */}
      <Modal
        title={t('sectionType.import.title')}
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>{t('sectionType.import.description')}</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            {t('sectionType.import.supportedFormats')}
          </p>
        </div>
        
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('sectionType.import.dragText')}</p>
          <p className="ant-upload-hint">
            {t('sectionType.import.hint')}
          </p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default SectionTypeList;